import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/Card/Card';
import { Input } from '@/components/ui/Input/Input';
import { Button } from '@/components/ui/Button/Button';
import { Alert } from '@/components/ui/Alert/Alert';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { AccountService } from '@/services/AccountService';
import { useNotificationStore } from '@/stores/notificationStore';

export function SecurityPage() {
  // Auto-capture token from URL query if present and auto-confirm
  React.useEffect(() => {
    (async () => {
      try {
        const url = new URL(window.location.href);
        const t = url.searchParams.get('emailToken');
        if (t) {
          setEmailBusy(true);
          await svc.confirmEmailChange(t);
          // Clean URL param
          url.searchParams.delete('emailToken');
          window.history.replaceState({}, '', url.pathname + (url.search ? '?' + url.search : ''));
          try { useNotificationStore.getState().success('Email updated'); } catch {}
          // Refresh profile email shown in UI
          await refreshCurrentEmail();
          setEmailStep('start');
        }
      } catch {
        try { useNotificationStore.getState().error('Verification link is invalid or expired'); } catch {}
      } finally {
        setEmailBusy(false);
      }
    })();
  }, []);
  const svc = React.useMemo(() => new AccountService(), []);
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [pwdError, setPwdError] = React.useState<string | null>(null);
  const [changing, setChanging] = React.useState(false);
  const [newEmail, setNewEmail] = React.useState('');
  const [currentEmail, setCurrentEmail] = React.useState<string>('');
  const [emailStep, setEmailStep] = React.useState<'start' | 'confirm'>('start');
  const [emailBusy, setEmailBusy] = React.useState(false);

  const refreshCurrentEmail = React.useCallback(async () => {
    try {
      const me = await svc.getProfile();
      setCurrentEmail(me.email);
    } catch {}
  }, [svc]);

  React.useEffect(() => { refreshCurrentEmail(); }, [refreshCurrentEmail]);

  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return { score: 0, label: '', color: '' };
    const checks = [
      pwd.length >= 12,
      /[a-z]/.test(pwd),
      /[A-Z]/.test(pwd),
      /[0-9]/.test(pwd),
      /[^A-Za-z0-9]/.test(pwd),
    ];
    const score = checks.filter(Boolean).length;
    const labels = ['', 'Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    const colors = ['', 'bg-destructive', 'bg-warning', 'bg-yellow-500', 'bg-success-500', 'bg-success'];
    return { score, label: labels[score], color: colors[score] };
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) return;
    setPwdError(null);
    
    if (newPassword !== confirmPassword) {
      setPwdError('Passwords do not match');
      return;
    }
    
    const tests = [
      { ok: newPassword.length >= 12, msg: 'At least 12 characters' },
      { ok: /[a-z]/.test(newPassword), msg: 'At least one lowercase letter' },
      { ok: /[A-Z]/.test(newPassword), msg: 'At least one uppercase letter' },
      { ok: /[0-9]/.test(newPassword), msg: 'At least one number' },
      { ok: /[^A-Za-z0-9]/.test(newPassword), msg: 'At least one symbol' },
    ];
    const failed = tests.find(t => !t.ok);
    if (failed) { setPwdError(failed.msg); return; }
    if (currentPassword === newPassword) { setPwdError('New password must differ from current password'); return; }
    
    try {
      setChanging(true);
      await svc.changePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      try { useNotificationStore.getState().success('Password changed successfully'); } catch {}
    } catch (err) {
      setPwdError('Failed to change password. Please check your current password.');
    } finally {
      setChanging(false);
    }
  };

  const handleStartEmailChange = async () => {
    if (!newEmail) return;
    try { 
      setEmailBusy(true); 
      await svc.startEmailChange(newEmail); 
      setEmailStep('confirm');
      try { useNotificationStore.getState().success(`Verification email sent to ${newEmail}`); } catch {}
    } catch (err) {
      try { useNotificationStore.getState().error('Failed to send verification email'); } catch {}
    }
    finally { setEmailBusy(false); }
  };

  const handleConfirmEmailChange = async () => {
    // This path is now auto-confirmed via URL; keep as no-op or future fallback
  };

  return (
    <PageContainer>
      <PageHeader 
        title="Security" 
        description="Manage your password and email authentication" 
      />

      <div className="space-y-6">

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
          <CardDescription>Choose a strong, unique password to protect your account</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Current password</label>
            <div className="relative">
              <Input 
                type={showCurrentPassword ? "text" : "password"} 
                value={currentPassword} 
                onChange={e => setCurrentPassword(e.target.value)} 
                placeholder="Enter your current password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showCurrentPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">New password</label>
            <div className="relative">
              <Input 
                type={showNewPassword ? "text" : "password"} 
                value={newPassword} 
                onChange={e => setNewPassword(e.target.value)} 
                placeholder="Enter your new password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showNewPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            
            {newPassword && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500">Password strength</span>
                  <span className="text-xs font-medium">{getPasswordStrength(newPassword).label}</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-300 ${getPasswordStrength(newPassword).color}`}
                    style={{ width: `${(getPasswordStrength(newPassword).score / 5) * 100}%` }}
                  />
                </div>
                <div className="mt-2 space-y-1 text-xs text-gray-500">
                  <div className={newPassword.length >= 12 ? 'text-success' : ''}>✓ 12+ characters</div>
                  <div className={/[a-z]/.test(newPassword) ? 'text-success' : ''}>✓ Lowercase letter</div>
                  <div className={/[A-Z]/.test(newPassword) ? 'text-success' : ''}>✓ Uppercase letter</div>
                  <div className={/[0-9]/.test(newPassword) ? 'text-success' : ''}>✓ Number</div>
                  <div className={/[^A-Za-z0-9]/.test(newPassword) ? 'text-success' : ''}>✓ Special character</div>
                </div>
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Confirm new password</label>
            <Input 
              type="password" 
              value={confirmPassword} 
              onChange={e => setConfirmPassword(e.target.value)} 
              placeholder="Confirm your new password"
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <div className="text-xs text-destructive mt-1">Passwords do not match</div>
            )}
          </div>
          
          {pwdError && (
            <Alert variant="error" title="Error">
              {pwdError}
            </Alert>
          )}
        </CardContent>
        <CardFooter>
          <Button 
            onClick={handleChangePassword} 
            disabled={changing || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
          >
            {changing ? 'Changing password...' : 'Change password'}
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Email Address</CardTitle>
          <CardDescription>Update your email address for account notifications and recovery</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="text-sm text-gray-600">Current email</div>
            <div className="text-sm font-medium">{currentEmail || '—'}</div>
          </div>
          <Alert variant="info">
            We'll send a verification link to your new email address. Click the link to complete the change.
          </Alert>
          <div>
            <label className="block text-sm font-medium mb-2">New email address</label>
            <Input 
              type="email" 
              value={newEmail} 
              onChange={e => setNewEmail(e.target.value)} 
              placeholder="name@example.com" 
            />
            <p className="text-xs text-gray-500 mt-1">
              Make sure you have access to this email address
            </p>
          </div>
        </CardContent>
        <CardFooter className="flex gap-2">
          <Button 
            onClick={handleStartEmailChange} 
            disabled={emailBusy || !newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)}
          >
            {emailBusy ? 'Sending verification...' : 'Send verification email'}
          </Button>
          {emailStep === 'confirm' && (
            <span className="text-sm text-gray-600">Awaiting confirmation via email…</span>
          )}
        </CardFooter>
      </Card>
      </div>
      </div>
    </PageContainer>
  );
}


